import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from 'react';
import { gql, useQuery } from '@apollo/client';
import { Image, Transformation } from 'cloudinary-react';
import {
  Theme,
  styled,
  Box,
  Divider,
  Toolbar,
  Typography,
  AppBar as MuiAppBar,
  Drawer as MuiDrawer,
  Tooltip,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import NewAnnouncementDialog from 'appDir/components/Shared/NewAnnouncementDialog';
import Loading from 'appDir/lib/components/Loading';
import GothamFonts from './GothamFonts';
import ProfileContainer from './UserProfile';
import { useShowGettingStarted } from './showGettingStarted';
import { useCookies } from 'react-cookie';

const MuiThemeUIJOrganizerProvider = props => <div {...props} />;

const NavOpenContext = createContext(false);

const drawerOpenWidth = 238;
const drawerCloseWidth = 84;

const openedMixin = (theme: Theme): any => ({
  width: drawerOpenWidth,
  border: 'none',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): any => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  border: 'none',
  overflowX: 'hidden',
  width: `calc(${drawerCloseWidth}px + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${drawerCloseWidth}px + 1px)`,
  },
});

export const DrawerHeader = styled('div')(
  ({ theme }) =>
    ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    } as any)
);

interface AppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme, open }) => {
  return {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerOpenWidth,
      width: `calc(100% - ${drawerOpenWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  };
});

const tOpts = `200ms ease-in-out`;

const useStylesLink = makeStyles({
  container: ({ navbarOpen, active }: any) =>
    ({
      cursor: 'pointer',
      border: '2px solid transparent',
      borderRadius: '5px',
      textDecoration: 'none',
      color: '#1E597F',
      transition: `background-color ${tOpts}, border ${tOpts}`,
      backgroundColor: active ? '#F4F6FD' : undefined,
      '& .sidenav-link-icon': {
        color: active ? '#048CDD' : '#D3D6E9',
        transition: `color ${tOpts}`,
      },
      '& .sidenav-link-title': {
        transition: `opacity ${tOpts}`,
        opacity: navbarOpen ? (active ? 1 : 0.7) : 0,
      },
      '&:hover': {
        border: '2px solid #F4F6FD',
      },
    } as any),
  label: {
    marginLeft: '15px',
    fontFamily: 'Gotham Rounded',
    fontStyle: 'normal',
    fontWeight: 350,
    fontSize: '13px',
    lineHeight: '16px',
  },
  tooltip: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '15px',
    boxShadow:
      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
  },
  tooltipLabel: {
    fontFamily: 'Inter, sans-serif',
    color: '#535453',
    fontSize: '13px',
    lineHeight: '16px',
  },
});

interface NavbarLinkProps {
  active?: boolean;
  href?: string;
  Icon: any;
  label: string;
  onClick?: () => void;
  style?: any;
}

const NavbarLink = ({
  active = false,
  href,
  Icon,
  label,
  onClick,
  style = {},
}: NavbarLinkProps) => {
  const navbarOpen = useContext(NavOpenContext);
  const classes = useStylesLink({ active, navbarOpen });
  const isSelectAPlan = label == 'Select A Plan';

  return (
    <Tooltip
      placement="right"
      style={{ backgroundColor: '#fff !important' }}
      classes={{ tooltip: classes.tooltip }}
      title={
        navbarOpen ? '' : <Box className={classes.tooltipLabel}>{label}</Box>
      }
    >
      <Box
        display="flex"
        p="6px"
        component="a"
        href={href}
        onClick={onClick}
        height="44px"
        className={classes.container}
        alignItems="center"
        style={style}
      >
        <Box width="24px" height="24px" className="sidenav-link-icon">
          {Icon}
        </Box>
        <Box
          className={`${classes.label} sidenav-link-title`}
          style={
            isSelectAPlan
              ? {
                  color: '#FF815A',
                  opacity: navbarOpen ? 1 : 0,
                }
              : undefined
          }
        >
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

const useStyles = makeStyles(
  theme =>
    ({
      container: props => ({
        display: 'flex',
      }),
      openButton: props => ({
        backgroundColor: '#F4F6FD',
        cursor: 'pointer',
        color: '#535453',
        border: 'none',
        borderRadius: '5px',
        height: '30px',
        width: '30px',
        padding: '3px',
        margin: '0 27px',
        ...(props.open && { display: 'none' }),
        transition: `background-color ${tOpts}`,
        '&:hover': {
          backgroundColor: '#DEF3FF',
        },
      }),
      drawer: props => ({
        width: drawerOpenWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(props.open ? openedMixin(theme) : closedMixin(theme)),
      }),
      drawerPaperOpen: openedMixin(theme),
      drawerPaperClosed: closedMixin(theme),
    } as any)
);

const isActive = (path: string) =>
  window ? window.location.pathname === path : false;

export interface SidenavUIJProps {
  hideAppBar?: boolean;
}

const SidenavUIJ: FC<SidenavUIJProps> = ({ hideAppBar = false }) => {
  const [newAnnouncementDialogOpen, setNewAnnouncementDialogOpen] = useState(
    false
  );
  const showGettingStarted = useShowGettingStarted();
  const [cookies, setCookie, removeCookie] = useCookies([
    'desktop_sidenav_closed',
  ]);
  const isSmall = useMediaQuery('(min-width: 1px) and (max-width: 1200px)');
  const open = !Boolean(cookies.desktop_sidenav_closed);
  const classes = useStyles({ open });
  const { data, loading } = useQuery(gql`
    query {
      currentOrganization {
        id
        hasSubscription
        logoFilename
        name
      }
    }
  `);

  const handleOpenSidenav = () => {
    removeCookie('desktop_sidenav_closed', { path: '/' });
  };

  const handleCloseSidenav = () => {
    setCookie('desktop_sidenav_closed', true, { path: '/' });
  };

  useEffect(() => {
    if (isSmall) handleCloseSidenav();
  }, [isSmall, setCookie]);

  if (loading) return <Loading />;
  if (!data) return null;

  const { logoFilename, name, hasSubscription } = data.currentOrganization;
  const showSelectAPlan = !hasSubscription;

  return (
    <MuiThemeUIJOrganizerProvider>
      <NavOpenContext.Provider value={open}>
        <Box className={classes.container}>
          {/* <CssBaseline /> */}
          {!hideAppBar && (
            <AppBar
              position="fixed"
              open={open}
              style={{ backgroundColor: '#ffffff', boxShadow: 'unset' }}
            >
              <Toolbar style={{ padding: '0', alignItems: 'center' }}>
                <Box
                  // type="button"
                  component="button"
                  onClick={handleOpenSidenav}
                  className={classes.openButton}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 8H17M7 12H13M7 16H9"
                      stroke="#535453"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Box height="36px" width="1px" border="1px solid #EFF1F8" />
                {Boolean(logoFilename) && (
                  <Image
                    publicId={logoFilename}
                    style={{ marginLeft: 29, height: 34 }}
                  >
                    <Transformation crop="scale" width="34" height="34" />
                  </Image>
                )}
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  style={{
                    marginLeft: '25px',
                    fontFamily: 'Gotham Rounded',
                    fontWeight: 700,
                    fontSize: '18px',
                    lineHeight: '22px',
                    color: '#535453',
                    flexGrow: 1,
                  }}
                >
                  {name}
                </Typography>
                {/* <NotificationIconContainer /> */}
                <ProfileContainer />
              </Toolbar>
            </AppBar>
          )}
          <MuiDrawer
            classes={{
              root: classes.drawer,
              paper: open ? classes.drawerPaperOpen : classes.drawerPaperClosed,
            }}
            variant="permanent"
            open={open}
          >
            <DrawerHeader>
              {open ? (
                <Box
                  onClick={handleCloseSidenav}
                  component="button"
                  style={{
                    backgroundColor: '#F4F6FD',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    padding: '3px',
                    marginRight: '15px',
                    width: '30px',
                    height: '30px',
                    transition: `background-color ${tOpts}`,
                    // '&:hover': {
                    //   backgroundColor: '#DEF3FF',
                    // },
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 17L10 12L14 7"
                      stroke="#535453"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              ) : (
                <Box
                  // type="button"
                  component="button"
                  onClick={handleOpenSidenav}
                  style={{
                    backgroundColor: '#F4F6FD',
                    cursor: 'pointer',
                    color: '#535453',
                    border: 'none',
                    borderRadius: '5px',
                    height: '30px',
                    width: '30px',
                    padding: '3px',
                    margin: 'auto',
                    display: open ? 'none' : undefined,
                    transition: `background-color ${tOpts}`,
                    // '&:hover': {
                    //   backgroundColor: '#DEF3FF',
                    // },
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 8H17M7 12H13M7 16H9"
                      stroke="#535453"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
              )}
            </DrawerHeader>
            <Box bgcolor="#F4F6FD">
              <Box padding="15px 22px" bgcolor="#fff">
                {showGettingStarted ? (
                  <NavbarLink
                    label="Get Started"
                    Icon={
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18.9822 3C15.9868 3 11.9707 5.05431 9.27277 8.58616H7.03569C5.42002 8.58616 4.49418 9.79277 3.85601 11.0678L3.72575 11.3277C3.39248 11.9927 3.87596 12.7758 4.61975 12.7758H6.62145C6.88668 12.7758 7.14105 12.8811 7.32858 13.0687L9.13033 14.8706L10.9321 16.6725C11.1196 16.86 11.225 17.1144 11.225 17.3796V19.3813C11.225 20.1249 12.0076 20.6083 12.6725 20.2756L12.9328 20.1453C14.2078 19.5085 15.4143 18.5812 15.4143 16.9654V14.7281C18.9458 12.03 21 8.01218 21 5.018V4C21 3.44772 20.5523 3 20 3H18.9822ZM15.4143 7.18962C15.7846 7.18962 16.1398 7.33675 16.4017 7.59866C16.6636 7.86056 16.8107 8.21577 16.8107 8.58616C16.8107 8.95654 16.6636 9.31176 16.4017 9.57366C16.1398 9.83556 15.7846 9.9827 15.4143 9.9827C15.0439 9.9827 14.6887 9.83556 14.4268 9.57366C14.165 9.31176 14.0178 8.95654 14.0178 8.58616C14.0178 8.21577 14.165 7.86056 14.4268 7.59866C14.6887 7.33675 15.0439 7.18962 15.4143 7.18962ZM7.03569 16.2671C6.65008 15.8815 6.02486 15.8815 5.63926 16.2671C5.1156 16.7908 4.75921 17.7123 4.53758 18.4772C4.3535 19.1124 4.89299 19.6698 5.52981 19.4911C6.27033 19.2833 7.16407 18.9318 7.7339 18.3619C8.11952 17.9763 8.11952 17.351 7.7339 16.9654L7.03569 16.2671Z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                    href="/admin/getting_started"
                  />
                ) : (
                  <NavbarLink
                    label="Dashboard"
                    Icon={
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="2" y="2" width="9" height="9" rx="3" />
                        <rect x="13" y="2" width="9" height="9" rx="3" />
                        <rect x="2" y="13" width="9" height="9" rx="3" />
                        <rect x="13" y="13" width="9" height="9" rx="3" />
                      </svg>
                    }
                    href="/admin/dashboard"
                  />
                )}
                <Divider style={{ borderColor: '#EFF1F8', margin: '15px 0' }} />
                {showSelectAPlan && (
                  <>
                    <NavbarLink
                      label="Select A Plan"
                      Icon={
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M8 10L8 8C8 5.79086 9.79086 4 12 4C13.934 4 15.5494 5.37356 15.92 7.19893C16.0298 7.74018 16.5577 8.08988 17.0989 7.98001C17.6402 7.87015 17.9899 7.34231 17.88 6.80107C17.324 4.06196 14.9039 2 12 2C8.68629 2 6 4.68629 6 8L6 10.1707C4.83481 10.5825 4 11.6938 4 13V19C4 20.6569 5.34315 22 7 22H17C18.6569 22 20 20.6569 20 19V13C20 11.3431 18.6569 10 17 10H8Z"
                            fill="#FF815A"
                          />
                          <circle cx="12" cy="14.5" r="1.5" fill="white" />
                          <path
                            d="M12 15L12 18"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      }
                      href="/admin/subscription/new"
                    />
                    <Divider
                      style={{ borderColor: '#EFF1F8', margin: '15px 0' }}
                    />
                  </>
                )}
                <NavbarLink
                  label="Website Builder"
                  active={isActive('/')}
                  href="/"
                  Icon={
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="2" y="2" width="20" height="16" rx="3" />
                      <rect
                        x="8.5"
                        y="20.5"
                        width="7"
                        height="1"
                        rx="0.5"
                        stroke="currentColor"
                      />
                    </svg>
                  }
                />
                <Box mt="5px">
                  <NavbarLink
                    label="Registration"
                    Icon={
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5 4C3.34315 4 2 5.34315 2 7V9H22V7C22 5.34315 20.6569 4 19 4H5ZM22 11H2V17C2 18.6569 3.34315 20 5 20H19C20.6569 20 22 18.6569 22 17V11Z"
                        />
                      </svg>
                    }
                    href="/admin/programs"
                  />
                </Box>
                <Box mt="5px">
                  <NavbarLink
                    label="Contact Manager"
                    Icon={
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M19 2H5C3.3 2 2 3.3 2 5V19C2 20.7 3.3 22 5 22H19C20.7 22 22 20.7 22 19V5C22 3.3 20.7 2 19 2ZM16 18C15.4 18 15 17.6 15 17C15 15.3 13.7 14 12 14C10.3 14 9 15.3 9 17C9 17.6 8.6 18 8 18C7.4 18 7 17.6 7 17C7 15.3 7.9 13.8 9.2 12.9C8.5 12.1 8 11.1 8 10C8 7.8 9.8 6 12 6C14.2 6 16 7.8 16 10C16 11.1 15.5 12.1 14.8 12.9C16.1 13.8 17 15.3 17 17C17 17.6 16.6 18 16 18Z" />
                        <path d="M12 12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C10.8954 8 10 8.89543 10 10C10 11.1046 10.8954 12 12 12Z" />
                      </svg>
                    }
                    href="/admin/contacts"
                    active={isActive('/admin/contacts')}
                  />
                </Box>
                <Box mt="5px">
                  <NavbarLink
                    label="Send Message"
                    onClick={() => {
                      setNewAnnouncementDialogOpen(true);
                    }}
                    Icon={
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 2H5C3.3 2 2 3.3 2 5V18V19.1C2 22 5.7 23.2 7.4 20.8L8.6 19.2C9.2 18.5 10.1 18 11 18H19C20.7 18 22 16.7 22 15V5C22 3.3 20.7 2 19 2ZM13 13H8C7.4 13 7 12.6 7 12C7 11.4 7.4 11 8 11H13C13.6 11 14 11.4 14 12C14 12.6 13.6 13 13 13ZM16 9H8C7.4 9 7 8.6 7 8C7 7.4 7.4 7 8 7H16C16.6 7 17 7.4 17 8C17 8.6 16.6 9 16 9Z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                  />
                </Box>
                <Box mt="5px">
                  <NavbarLink
                    label="Background Checks"
                    Icon={
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M19 2H5C3.3 2 2 3.3 2 5V15V19C2 20.7 3.3 22 5 22H19C20.7 22 22 20.7 22 19V15V5C22 3.3 20.7 2 19 2ZM8.7 9.2C9.1 8.8 9.7 8.8 10.1 9.2L11.5 10.6L14.3 7.8C14.5 7.6 14.7 7.5 15 7.5C15.3 7.5 15.5 7.6 15.7 7.8C16.1 8.2 16.1 8.8 15.7 9.2L12.2 12.7C11.9 13 11.4 13.1 11.1 12.9C11 12.9 10.9 12.8 10.8 12.7L8.7 10.7C8.3 10.3 8.3 9.6 8.7 9.2ZM20 19C20 19.6 19.6 20 19 20H5C4.4 20 4 19.6 4 19V17.8C4.3 17.9 4.6 18 5 18H19C19.4 18 19.7 17.9 20 17.8V19Z"
                          fill="currentColor"
                        />
                      </svg>
                    }
                    href="/admin/background_checks"
                  />
                </Box>
                <Divider style={{ borderColor: '#EFF1F8', margin: '15px 0' }} />
                <NavbarLink
                  label="Settings"
                  Icon={
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.9838 2.54161C14.0711 2.71093 14.0928 2.92777 14.1361 3.36144C14.2182 4.1823 14.2593 4.59274 14.4311 4.81793C14.649 5.10358 15.0034 5.25038 15.3595 5.20248C15.6402 5.16472 15.9594 4.90352 16.5979 4.38113C16.9352 4.10515 17.1038 3.96716 17.2853 3.90918C17.5158 3.83555 17.7652 3.84798 17.9872 3.94419C18.162 4.01994 18.3161 4.17402 18.6243 4.4822L19.5178 5.37567C19.8259 5.68385 19.98 5.83794 20.0558 6.01275C20.152 6.23478 20.1644 6.48415 20.0908 6.71464C20.0328 6.89612 19.8948 7.06478 19.6188 7.4021C19.0964 8.0406 18.8352 8.35984 18.7975 8.64056C18.7496 8.99662 18.8964 9.35102 19.182 9.56893C19.4072 9.74072 19.8176 9.78176 20.6385 9.86385C21.0722 9.90722 21.2891 9.92891 21.4584 10.0162C21.6734 10.1272 21.841 10.3123 21.9299 10.5373C22 10.7145 22 10.9324 22 11.3682V12.6319C22 13.0676 22 13.2855 21.93 13.4626C21.841 13.6877 21.6734 13.8729 21.4583 13.9838C21.289 14.0711 21.0722 14.0928 20.6386 14.1361L20.6386 14.1361C19.818 14.2182 19.4077 14.2592 19.1825 14.4309C18.8967 14.6489 18.7499 15.0034 18.7979 15.3596C18.8357 15.6402 19.0968 15.9593 19.619 16.5976C19.8949 16.9348 20.0328 17.1034 20.0908 17.2848C20.1645 17.5154 20.152 17.7648 20.0558 17.9869C19.98 18.1617 19.826 18.3157 19.5179 18.6238L18.6243 19.5174C18.3162 19.8255 18.1621 19.9796 17.9873 20.0554C17.7652 20.1516 17.5159 20.164 17.2854 20.0904C17.1039 20.0324 16.9352 19.8944 16.5979 19.6184L16.5979 19.6184C15.9594 19.096 15.6402 18.8348 15.3595 18.7971C15.0034 18.7492 14.649 18.896 14.4311 19.1816C14.2593 19.4068 14.2183 19.8173 14.1362 20.6383C14.0928 21.0722 14.0711 21.2891 13.9837 21.4585C13.8728 21.6735 13.6877 21.8409 13.4628 21.9299C13.2856 22 13.0676 22 12.6316 22H11.3682C10.9324 22 10.7145 22 10.5373 21.9299C10.3123 21.841 10.1272 21.6734 10.0162 21.4584C9.92891 21.2891 9.90722 21.0722 9.86385 20.6385C9.78176 19.8176 9.74072 19.4072 9.56892 19.182C9.35101 18.8964 8.99663 18.7496 8.64057 18.7975C8.35985 18.8352 8.04059 19.0964 7.40208 19.6189L7.40206 19.6189C7.06473 19.8949 6.89607 20.0329 6.71458 20.0908C6.4841 20.1645 6.23474 20.152 6.01272 20.0558C5.8379 19.9801 5.6838 19.826 5.37561 19.5178L4.48217 18.6243C4.17398 18.3162 4.01988 18.1621 3.94414 17.9873C3.84794 17.7652 3.8355 17.5159 3.90913 17.2854C3.96711 17.1039 4.10511 16.9352 4.3811 16.5979C4.90351 15.9594 5.16471 15.6402 5.20247 15.3594C5.25037 15.0034 5.10357 14.649 4.81792 14.4311C4.59273 14.2593 4.1823 14.2182 3.36143 14.1361C2.92776 14.0928 2.71093 14.0711 2.54161 13.9838C2.32656 13.8728 2.15902 13.6877 2.07005 13.4627C2 13.2855 2 13.0676 2 12.6318V11.3683C2 10.9324 2 10.7144 2.07008 10.5372C2.15905 10.3123 2.32654 10.1272 2.54152 10.0163C2.71088 9.92891 2.92777 9.90722 3.36155 9.86384H3.36155H3.36156C4.18264 9.78173 4.59319 9.74068 4.81842 9.56881C5.10395 9.35092 5.2507 8.99664 5.20287 8.64066C5.16514 8.35987 4.90385 8.04052 4.38128 7.40182C4.10516 7.06435 3.96711 6.89561 3.90914 6.71405C3.83557 6.48364 3.848 6.23438 3.94413 6.01243C4.01988 5.83754 4.17403 5.68339 4.48233 5.37509L5.37565 4.48177L5.37566 4.48177C5.68385 4.17357 5.83795 4.01947 6.01277 3.94373C6.23478 3.84753 6.48414 3.8351 6.71463 3.90872C6.89612 3.9667 7.06481 4.10472 7.4022 4.38076C8.04061 4.9031 8.35982 5.16427 8.64044 5.20207C8.99661 5.25003 9.35113 5.10319 9.56907 4.81742C9.74077 4.59227 9.78181 4.18195 9.86387 3.36131C9.90722 2.92776 9.9289 2.71098 10.0162 2.5417C10.1271 2.32658 10.3123 2.15898 10.5374 2.07001C10.7145 2 10.9324 2 11.3681 2H12.6318C13.0676 2 13.2855 2 13.4627 2.07005C13.6877 2.15902 13.8728 2.32656 13.9838 2.54161ZM12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"
                        fill="currentColor"
                      />
                    </svg>
                  }
                  href="/admin/settings"
                />
                <NavbarLink
                  onClick={() => {
                    if (window.Beacon) window.Beacon('toggle');
                  }}
                  label="Help"
                  style={{ marginTop: '5px' }}
                  Icon={
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM12 18C11.4 18 11 17.6 11 17C11 16.4 11.4 16 12 16C12.6 16 13 16.4 13 17C13 17.6 12.6 18 12 18ZM13.3 13C13 13.3 13 13.4 13 14C13 14.6 12.6 15 12 15C11.4 15 11 14.6 11 14C11 13.3 11 12.3 12.1 11.5C12.7 10.9 13 10.7 13 10C13 9.6 12.8 9.3 12.4 9.1C12.4 9.1 12.2 9.1 12 9C11.7 9 11.5 9.1 11.4 9.2C11 9.6 11 10 11 10.1C11 10.6 10.5 11 9.9 11C9.4 11 9 10.5 9 10C9 9.9 9.1 8.8 10 7.8C10.5 7.3 11.2 7 12 7C12.6 7 13.1 7.1 13.5 7.3C14.5 7.9 15 8.9 15 10C15 11.6 14 12.4 13.3 13Z" />
                    </svg>
                  }
                />
              </Box>
            </Box>
          </MuiDrawer>
          <NewAnnouncementDialog
            open={newAnnouncementDialogOpen}
            onClose={() => setNewAnnouncementDialogOpen(false)}
          />
        </Box>
      </NavOpenContext.Provider>
      <GothamFonts />
    </MuiThemeUIJOrganizerProvider>
  );
};

export default SidenavUIJ;
